/**
 * Client-side decryption using Web Crypto API
 * For view-only web client - decryption only
 */

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;

/**
 * Import key from base64 string (from URL fragment)
 */
export async function importKeyFromString(keyString: string): Promise<CryptoKey> {
  // Convert URL-safe base64 to standard base64 (Android uses URL_SAFE encoding for keys)
  const standardBase64 = keyString.replace(/-/g, '+').replace(/_/g, '/');
  const keyData = Uint8Array.from(atob(standardBase64), (c) => c.charCodeAt(0));
  return await crypto.subtle.importKey(
    'raw',
    keyData,
    {
      name: ALGORITHM,
      length: KEY_LENGTH,
    },
    false,
    ['decrypt']
  );
}

/**
 * Decrypt data (from base64)
 */
export async function decryptData(
  encryptedData: string,
  key: CryptoKey
): Promise<string> {
  // Decode from base64
  const combined = Uint8Array.from(atob(encryptedData), (c) => c.charCodeAt(0));

  // Extract IV and encrypted data
  const iv = combined.slice(0, IV_LENGTH);
  const encrypted = combined.slice(IV_LENGTH);

  // Decrypt
  const decryptedBuffer = await crypto.subtle.decrypt(
    {
      name: ALGORITHM,
      iv: iv,
    },
    key,
    encrypted
  );

  // Convert to string
  const decoder = new TextDecoder();
  return decoder.decode(decryptedBuffer);
}

/**
 * Decrypt location data
 */
export async function decryptLocationData(
  encryptedData: string,
  key: CryptoKey
): Promise<{ lat: number; lng: number }> {
  const decrypted = await decryptData(encryptedData, key);
  return JSON.parse(decrypted);
}
